import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';

/**
 * The "More" menu on the client invoice header (D98.2).
 *
 * One accessible menu replaces the row of header buttons. It only lays items out: each item
 * arrives with the page's own handler and gating (`hidden` / `disabled` / `title`), so the menu
 * decides nothing about who may do what. An item with `to` is a plain link, not a button.
 */
export interface MoreMenuItem {
  key: string;
  label: string;
  onClick?: () => void;
  /** A plain link instead of a button (e.g. the new credit note). */
  to?: string;
  /** Link items only: runs on click before navigating (preventDefault to stop it) - the page's unsaved-changes guard. */
  onNavigate?: (e: React.MouseEvent, to: string) => void;
  disabled?: boolean;
  title?: string;
  /** Left out entirely - the page's permission / status gate said no. */
  hidden?: boolean;
}

const ITEM = 'block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white';

export const MoreMenu: React.FC<{ items: MoreMenuItem[] }> = ({ items }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <Button aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        More
      </Button>
      {open && (
        <div
          role="menu"
          aria-label="More actions"
          className="absolute right-0 top-full z-30 mt-1 min-w-[220px] rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {items.filter((i) => !i.hidden).map((i) =>
            i.to ? (
              <Link key={i.key} role="menuitem" to={i.to} title={i.title} className={ITEM} onClick={(e) => {
                  setOpen(false);
                  i.onNavigate?.(e, i.to!);
                }}>
                {i.label}
              </Link>
            ) : (
              <button
                key={i.key}
                type="button"
                role="menuitem"
                disabled={i.disabled}
                title={i.title}
                className={ITEM}
                onClick={() => {
                  setOpen(false);
                  i.onClick?.();
                }}
              >
                {i.label}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};
